import { useEffect, useMemo, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EditorialMetaCard } from '@/components/content/EditorialMetaCard';
import { getCalculatorSeoContent } from '@/content/calculator-seo-content';
import { getEditorialMetadata } from '@/content/editorial-metadata';

const EMBED_MARKER = '[CALCULATOR EMBED]';
const HORIZONTAL_RULE = /^-{3,}$/;

type Placement = 'before' | 'after';

type Props = {
  canonicalPath: string;
  placement: Placement;
};

type SplitContent = {
  beforeEmbed: string;
  afterEmbed: string;
  markerFound: boolean;
};

type GuideSection = {
  id: string;
  label: string;
  markdown: string;
};

function stripMarkdownSyntax(value: string): string {
  return value
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/\\([\\`*_{}\[\]()#+\-.!])/g, '$1')
    .trim();
}

function slugifyHeading(value: string): string {
  const normalized = stripMarkdownSyntax(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

  return normalized || 'section';
}

function createSlugFactory() {
  const counts = new Map<string, number>();

  return (label: string) => {
    const base = slugifyHeading(label);
    const count = (counts.get(base) ?? 0) + 1;
    counts.set(base, count);
    return count === 1 ? base : `${base}-${count}`;
  };
}

function dropTrailingSeparators(lines: string[]): string[] {
  let end = lines.length;

  while (end > 0 && lines[end - 1].trim() === '') {
    end -= 1;
  }

  if (end > 0 && HORIZONTAL_RULE.test(lines[end - 1].trim())) {
    end -= 1;
    while (end > 0 && lines[end - 1].trim() === '') {
      end -= 1;
    }
  }

  return lines.slice(0, end);
}

function dropLeadingSeparators(lines: string[]): string[] {
  let start = 0;

  while (start < lines.length && lines[start].trim() === '') {
    start += 1;
  }

  if (start < lines.length && HORIZONTAL_RULE.test(lines[start].trim())) {
    start += 1;
    while (start < lines.length && lines[start].trim() === '') {
      start += 1;
    }
  }

  return lines.slice(start);
}

function splitMarkdown(markdown: string): SplitContent {
  const normalized = markdown.replace(/\r\n/g, '\n');
  const lines = normalized.split('\n');
  const markerIndex = lines.findIndex((line) => line.trim() === EMBED_MARKER);

  if (markerIndex < 0) {
    return {
      beforeEmbed: normalized.trim(),
      afterEmbed: '',
      markerFound: false
    };
  }

  const beforeEmbed = dropTrailingSeparators(lines.slice(0, markerIndex)).join('\n').trim();
  const afterEmbed = dropLeadingSeparators(lines.slice(markerIndex + 1)).join('\n').trim();

  return {
    beforeEmbed,
    afterEmbed,
    markerFound: true
  };
}

function parseGuideSections(markdown: string): GuideSection[] {
  const normalized = markdown.replace(/\r\n/g, '\n').trim();
  if (!normalized) {
    return [];
  }

  const lines = normalized.split('\n');
  const headingMatches: Array<{ lineIndex: number; label: string }> = [];

  lines.forEach((line, index) => {
    const match = line.match(/^##\s+(.+)$/);
    if (match) {
      headingMatches.push({ lineIndex: index, label: stripMarkdownSyntax(match[1]) });
    }
  });

  if (headingMatches.length === 0) {
    return [{ id: 'guide', label: 'Guide', markdown: normalized }];
  }

  const createId = createSlugFactory();

  return headingMatches
    .map((entry, index) => {
      const nextLineIndex = headingMatches[index + 1]?.lineIndex ?? lines.length;
      const sectionLines = dropLeadingSeparators(lines.slice(entry.lineIndex + 1, nextLineIndex));
      const markdownBody = dropTrailingSeparators(sectionLines).join('\n').trim();

      return {
        id: createId(entry.label),
        label: entry.label,
        markdown: markdownBody
      };
    })
    .filter((section) => section.label);
}

function flattenText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return `${node}`;
  }

  if (Array.isArray(node)) {
    return node.map((item) => flattenText(item)).join('');
  }

  if (node && typeof node === 'object' && 'props' in node) {
    const maybeNode = node as { props?: { children?: ReactNode } };
    return flattenText(maybeNode.props?.children);
  }

  return '';
}

function hasProtocol(href: string): boolean {
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href) || href.startsWith('//');
}

function normalizeInternalHref(rawHref: string): string {
  const href = rawHref.trim();
  if (!href || href.startsWith('#') || hasProtocol(href)) {
    return href;
  }

  const match = href.match(/^([^?#]*)(\?[^#]*)?(#.*)?$/);
  let pathPart = match?.[1] ?? href;
  const queryPart = match?.[2] ?? '';
  const hashPart = match?.[3] ?? '';

  pathPart = pathPart.replace(/^(\.\/)+/, '');
  while (pathPart.startsWith('../')) {
    pathPart = pathPart.slice(3);
  }

  if (pathPart === '/advanced-fire-calculator' || pathPart === 'advanced-fire-calculator') {
    pathPart = '/advanced-calculator';
  }

  if (pathPart.endsWith('/advanced-fire-calculator')) {
    pathPart = `${pathPart.slice(0, -'/advanced-fire-calculator'.length)}/advanced-calculator`;
  }

  if (pathPart.endsWith('.html')) {
    pathPart = pathPart.slice(0, -'.html'.length);
  }

  if (!pathPart) {
    return `${queryPart}${hashPart}`;
  }

  if (!pathPart.startsWith('/')) {
    pathPart = `/${pathPart}`;
  }

  if (pathPart !== '/' && !pathPart.endsWith('/')) {
    pathPart = `${pathPart}/`;
  }

  return `${pathPart}${queryPart}${hashPart}`;
}

function TrustSignalCard() {
  return (
    <Card className="brand-frame border-emerald-500/25 bg-emerald-500/5">
      <CardContent className="grid gap-3 p-4 text-sm text-muted-foreground md:grid-cols-3">
        <p>
          <span className="font-semibold text-foreground">Private by default:</span> calculator inputs run in your browser and share links encode state locally.
        </p>
        <p>
          <span className="font-semibold text-foreground">Assumption-led:</span> outputs are scenarios, not guarantees, so compare baseline and conservative cases before acting.
        </p>
        <p>
          <span className="font-semibold text-foreground">Reviewed references:</span> methodology notes and source links are included below each calculator guide.
        </p>
      </CardContent>
    </Card>
  );
}

export function CalculatorSeoArticle({ canonicalPath, placement }: Props) {
  const rawContent = getCalculatorSeoContent(canonicalPath);
  const parsed = useMemo(() => (rawContent ? splitMarkdown(rawContent) : null), [rawContent]);
  const editorialMetadata = useMemo(() => getEditorialMetadata(canonicalPath), [canonicalPath]);

  const introMarkdown = useMemo(() => {
    if (!parsed) {
      return '';
    }

    return parsed.beforeEmbed;
  }, [parsed]);

  const guideMarkdown = useMemo(() => {
    if (!parsed) {
      return '';
    }

    if (!parsed.markerFound && placement === 'before') {
      return parsed.beforeEmbed;
    }

    if (!parsed.markerFound && placement === 'after') {
      return '';
    }

    return parsed.afterEmbed;
  }, [parsed, placement]);

  const guideSections = useMemo(() => parseGuideSections(guideMarkdown), [guideMarkdown]);

  const [openSectionIds, setOpenSectionIds] = useState<string[]>([]);

  useEffect(() => {
    if (placement !== 'after') {
      setOpenSectionIds([]);
      return;
    }

    if (guideSections.length === 0) {
      setOpenSectionIds([]);
      return;
    }

    setOpenSectionIds((previous) => {
      const stillValid = previous.filter((id) => guideSections.some((section) => section.id === id));
      return stillValid.length > 0 ? stillValid : guideSections.map((section) => section.id);
    });
  }, [guideSections, placement]);

  useEffect(() => {
    if (placement !== 'after' || guideSections.length === 0 || typeof window === 'undefined') {
      return;
    }

    const openFromHash = () => {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, '')).trim();
      if (!hash) {
        return;
      }

      const target = guideSections.find((section) => section.id === hash);
      if (!target) {
        return;
      }

      setOpenSectionIds((previous) => (previous.includes(target.id) ? previous : [...previous, target.id]));
    };

    openFromHash();
    window.addEventListener('hashchange', openFromHash);

    return () => {
      window.removeEventListener('hashchange', openFromHash);
    };
  }, [guideSections, placement]);

  const toggleSection = (id: string) => {
    setOpenSectionIds((previous) => (previous.includes(id) ? previous.filter((value) => value !== id) : [...previous, id]));
  };

  if (!parsed) {
    return null;
  }

  if (placement === 'before') {
    if (!introMarkdown) {
      return null;
    }

    const createId = createSlugFactory();

    return (
      <section className="grid gap-4">
        <Card className="brand-frame border-border/80 bg-card/92">
          <CardContent className="pt-6">
            <div className="calculator-seo-prose calculator-seo-intro">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => {
                    const id = createId(flattenText(children));
                    return (
                      <h2 id={id} className="calculator-seo-h2">
                        {children}
                      </h2>
                    );
                  },
                  h2: ({ children }) => {
                    const id = createId(flattenText(children));
                    return (
                      <h3 id={id} className="calculator-seo-h3">
                        {children}
                      </h3>
                    );
                  },
                  h3: ({ children }) => {
                    const id = createId(flattenText(children));
                    return (
                      <h4 id={id} className="calculator-seo-h4">
                        {children}
                      </h4>
                    );
                  },
                  a: ({ href = '', children }) => {
                    const normalizedHref = normalizeInternalHref(href);
                    const isExternal = normalizedHref.startsWith('//') || /^https?:\/\//i.test(normalizedHref);
                    return (
                      <a
                        href={normalizedHref}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noreferrer noopener' : undefined}
                      >
                        {children}
                      </a>
                    );
                  }
                }}
              >
                {introMarkdown}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>
        <TrustSignalCard />
      </section>
    );
  }

  if (guideSections.length === 0) {
    return placement === 'after' && editorialMetadata ? <EditorialMetaCard metadata={editorialMetadata} title="Editorial Review and Sources" /> : null;
  }

  return (
    <section className="grid gap-4">
      <Card className="brand-frame border-border/80 bg-card/92">
        <CardHeader>
          <CardTitle className="font-display text-2xl leading-tight text-foreground">Guide</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="grid gap-2 md:grid-cols-2">
            {guideSections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="calculator-seo-jump-link"
                onClick={() => {
                  setOpenSectionIds((previous) => (previous.includes(section.id) ? previous : [...previous, section.id]));
                }}
              >
                {section.label}
              </a>
            ))}
          </div>

          <div className="calculator-guide-stack">
            {guideSections.map((section) => {
              const isOpen = openSectionIds.includes(section.id);
              const triggerId = `${section.id}-trigger`;
              const panelId = `${section.id}-panel`;
              const createId = createSlugFactory();

              return (
                <section key={section.id} id={section.id} className="calculator-guide-section">
                  <h3 className="m-0">
                    <button
                      type="button"
                      id={triggerId}
                      className="calculator-guide-trigger"
                      aria-controls={panelId}
                      aria-expanded={isOpen}
                      onClick={() => toggleSection(section.id)}
                    >
                      <span>{section.label}</span>
                      <span className={`calculator-guide-icon${isOpen ? ' is-open' : ''}`} aria-hidden="true">
                        ▾
                      </span>
                    </button>
                  </h3>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={triggerId}
                    className="calculator-guide-panel"
                    hidden={!isOpen}
                  >
                    {section.markdown ? (
                      <div className="calculator-seo-prose calculator-seo-guide-prose">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: ({ children }) => {
                              const id = `${section.id}-${createId(flattenText(children))}`;
                              return (
                                <h3 id={id} className="calculator-seo-h3">
                                  {children}
                                </h3>
                              );
                            },
                            h2: ({ children }) => {
                              const id = `${section.id}-${createId(flattenText(children))}`;
                              return (
                                <h3 id={id} className="calculator-seo-h3">
                                  {children}
                                </h3>
                              );
                            },
                            h3: ({ children }) => {
                              const id = `${section.id}-${createId(flattenText(children))}`;
                              return (
                                <h4 id={id} className="calculator-seo-h4">
                                  {children}
                                </h4>
                              );
                            },
                            h4: ({ children }) => {
                              const id = `${section.id}-${createId(flattenText(children))}`;
                              return (
                                <h4 id={id} className="calculator-seo-h4">
                                  {children}
                                </h4>
                              );
                            },
                            a: ({ href = '', children }) => {
                              const normalizedHref = normalizeInternalHref(href);
                              const isExternal = normalizedHref.startsWith('//') || /^https?:\/\//i.test(normalizedHref);
                              return (
                                <a
                                  href={normalizedHref}
                                  target={isExternal ? '_blank' : undefined}
                                  rel={isExternal ? 'noreferrer noopener' : undefined}
                                >
                                  {children}
                                </a>
                              );
                            }
                          }}
                        >
                          {section.markdown}
                        </ReactMarkdown>
                      </div>
                    ) : null}
                  </div>
                </section>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {editorialMetadata ? <EditorialMetaCard metadata={editorialMetadata} title="Editorial Review and Sources" /> : null}
    </section>
  );
}
